import { 
	AppAction, 
	Namespace as AppActionNS 
} from '../app.actions';
import { FolderAction } from 'sharebase-app/components/folderView/folderView.actions';
import { 
	DualMenuItem,
	MenuPriority
} from 'sharebase/domain';
import {
	ShareBaseItem,
	ShareBaseDocument,
	pxItemTypeGuards,
	EditableWopiFiles
} from 'phoenix/domain';
import {
	AuthService,
	EnvironmentService,
	PhoenixToggleService
} from 'phoenix/services';
import { MenuItemFactory } from 'sharebase/factories';
import { ToolbarService } from 'sharebase-app/services/appToolBarService';
import { SelectionDataStore } from 'sharebase-app/dataStores/selectionDataStore';
import { 
	ActionRepository,
	IDesktopService,
	ContextMenuService,
	ToastService,
	TrackingService
} from 'sharebase/services';
import { StaticToggleService } from 'util/services/staticToggleService';

export default function registerMenuItems(
	$mdMedia:ng.material.IMedia,
	$state:ng.ui.IStateService,
	$stateParams:ng.ui.IStateParamsService,
	$window:ng.IWindowService,
	pxAuthService:AuthService,
	pxEnvironmentService:EnvironmentService,
	pxPhoenixToggleService:PhoenixToggleService,
	sbDesktopService:IDesktopService,
	sbActionRepository:ActionRepository,
	sbContextMenuService:ContextMenuService,
	sbToastService:ToastService,
	sbTrackingService:TrackingService,
	sbMenuItemFactory:MenuItemFactory,
	SBA_GET_REQUEST_RIGHTS_ENABLED:() => boolean,
	sbaToolbarService:ToolbarService,
	sbaSelectionDataStore:SelectionDataStore,
	staticToggleService:StaticToggleService,
	gettextCatalog:ng.gettext.gettextCatalog
){
	/*------ Global Context Menu Items -----*/
	let requestRightsEnabled = SBA_GET_REQUEST_RIGHTS_ENABLED();
	let contextMenuItems = sbContextMenuService.items;
	let wopiToggleEnabled = pxPhoenixToggleService.isEnabled('Core/WOPIEdit');
	sbActionRepository
		.getActions(AppActionNS)
		// only sharing documents from a contextmenu is supported at this time
		// due to singleton datastore archiecture;  
		// don't construct favorite menu items due to toggleable nature
		.filter(a => {
			let include = true;
			switch(a.action){
				case AppAction.CreateFolderLinkShare:  
				case AppAction.CreateFolderUserShare: 
				case AppAction.Favorite:  
				case AppAction.Unfavorite:
					include = false;
					break;
			}
			return include;
		})
		.map(action => sbActionRepository.createFromAction(action, !requestRightsEnabled))	
		.forEach(i => sbContextMenuService.addItem(i));

	if(staticToggleService.isEnabled('nested-context-menu')){
		const actions = [
			AppAction.CreateDocumentLinkShare,
			AppAction.CreateDocumentUserShare
		];
		const sharingGroup = sbMenuItemFactory.createNested('sharing', gettextCatalog.getString('Share'), 'share');

		sbActionRepository
			.getActions(actions)
			.map(a => sbActionRepository.createFromAction(a))
			.forEach(item => sharingGroup.menu.addItem(item));
		sbContextMenuService.removeItems(...actions);
		sbContextMenuService.addItem(sharingGroup);
	}
		
	let favoriteItem = sbActionRepository
		.getActions([AppAction.Favorite, AppAction.Unfavorite])
		.map(a => sbActionRepository.createFromAction(a, !requestRightsEnabled))
		.reduce((dualItem, item, i, arr) => {
			return dualItem || sbMenuItemFactory.createDual(AppAction.Favorite, arr[0], arr[1]);
		}, null) as DualMenuItem;
	favoriteItem
		.getState(AppAction.Favorite)
		.addValidator((target:ShareBaseItem) => !target.IsFavorite);
	favoriteItem
		.getState(AppAction.Unfavorite)
		.addValidator((target:ShareBaseItem) => target.IsFavorite);
	sbContextMenuService.addItem(favoriteItem);
	
	contextMenuItems[AppAction.CreateTask].addValidator(target => pxPhoenixToggleService.isEnabled('Platform/Tasks'));
	contextMenuItems[AppAction.Download].addValidator((target:ShareBaseItem, all) => {
		// can only download 1 library at a time. And if downloading a library, the user
		// cannot choose anything else to download.
		let numLibs = all.filter(pxItemTypeGuards.isShareBaseLibrary).length;
		return numLibs === 0 || (numLibs === 1 && all.length === 1);
	});
	// with WOPIEdit on, we always want to surface edit in desktop as an option alongside Office Online.
	contextMenuItems[AppAction.EditInShareBaseDesktop].addValidator(target => {
		if (sbDesktopService.desktopAvailable) {
			return true;
		}
		else if (pxEnvironmentService.isWindows && !pxAuthService.isShareByLinkUser) {
			return wopiToggleEnabled;
		}
		else {
			return false;
		}
	});
	contextMenuItems[AppAction.EditInShareBaseDesktop].addTrigger((target:ShareBaseDocument) => {
		if (sbDesktopService.polling) {
			sbTrackingService.trackClick(`Desktop Edit context - Desktop polling`)
			sbToastService.showSimple(gettextCatalog.getString('Connecting to ShareBase Desktop...'));
		}
		else {
			sbDesktopService.edit(target)
				.then(() => sbTrackingService.trackClick(`Desktop Edit context - Desktop connected`))
				.catch(e => {
					sbTrackingService.trackClick(`Desktop Edit context - Desktop not discovered`)
					sbToastService.showSimple(sbDesktopService.createErrorMessage(e))
				});
		}
	});
	contextMenuItems[AppAction.EditInOfficeOnline].addValidator((target:ShareBaseDocument) => {
		if (EditableWopiFiles.has(target.DocumentName.split('.').pop()) && !pxAuthService.isShareByLinkUser && $mdMedia('gt-sm')) {
			return wopiToggleEnabled;
		}
		else {
			return false;
		}
	});
	contextMenuItems[AppAction.EditInOfficeOnline].addTrigger((document:ShareBaseDocument) => {
		if (pxAuthService.user.UserName !== document.Owner) {
			sbTrackingService.trackClick('office online edit - shared document');
		}
		sbTrackingService.addCustomDimension('Wopi Edit User', pxAuthService.userId);
		$window.open($state.href('officeOnline', {documentId: document.DocumentId}));
	});

	contextMenuItems[AppAction.OpenInNew].addTrigger((target:ShareBaseItem) => {
		let uri;
		let route;
		let params:any = {};

		if(pxItemTypeGuards.isShareBaseDocument(target)){
			params = {
				docID: target.DocumentId
			};
			route = 'content.myFiles.document';
		}
		else if(pxItemTypeGuards.isShareBaseFolder(target)){
			params =  {
				folderID: target.FolderId
			};
			route = 'content.myFiles.folder';
		}
		
		if(pxAuthService.isShareByLinkUser){
			route += '.share';
			// If share by link user, make sure the user goes to
			// the share route and the token remains on the url
			params.token = $stateParams.token;
			params.recipient = $stateParams.recipient;
			pxAuthService.lastLinkPassword = $stateParams.password;
		}

		uri = $state.href(route, params);
		
		if(uri){
			$window.open(uri);
		}				
	});

	/*---------- Global toolbar items ---------*/	
	Object.keys(contextMenuItems).forEach(k => copyToToolbar(k));
	if(staticToggleService.isEnabled('minimized-action-bar')){
		const actions = [
			AppAction.CreateFolderLinkShare,
			AppAction.CreateFolderUserShare,
			AppAction.CreateDocumentLinkShare,
			AppAction.CreateDocumentUserShare
		];
		let sharingGroup = sbMenuItemFactory.createNested('sharing', gettextCatalog.getString('Share'), 'share');
		sharingGroup.priority = MenuPriority.HIGH;
		
		sbActionRepository
			.getActions(actions)
			.map(a => sbActionRepository.createFromAction(a))
			.forEach(item => sharingGroup.menu.addItem(item));
		sbaToolbarService.removeItems(...actions);
		sbaToolbarService.addItem(sharingGroup);
	}
	else {
		sbActionRepository
			.getActions([
				AppAction.CreateFolderLinkShare,
				AppAction.CreateFolderUserShare
			])
			.map(a => sbActionRepository.createFromAction(a, !requestRightsEnabled))
			.forEach(i => sbaToolbarService.addItem(i));
	}

	sbaToolbarService
		.setPriority(MenuPriority.HIGH,
			FolderAction.UploadDocument,
			FolderAction.OpenFolder,
			FolderAction.CreateFolder,
			FolderAction.OpenDocument,
			AppAction.EditInShareBaseDesktop,
			AppAction.CreateFolderLinkShare,
			AppAction.CreateDocumentLinkShare,
			AppAction.Download,
			AppAction.Replace,
			AppAction.CreateTask
		)
		.setPriority(MenuPriority.MEDIUMHIGH,
			AppAction.CreateDocumentUserShare
		)
		.setPriority(MenuPriority.MEDIUM, 			
			AppAction.Copy,
			AppAction.Rename,
			AppAction.Move
		)
		.setPriority(MenuPriority.MEDIUMLOW,
			AppAction.Favorite,
			AppAction.Unfavorite,
			AppAction.OpenInNew
		)
		.setPriority(MenuPriority.LOW, AppAction.Delete);
	
	/*----------- Global selection bar items ----------*/
	sbaSelectionDataStore.toolbar
		.setPriority(MenuPriority.HIGH, 
			AppAction.CreateDocumentLinkShare,
			AppAction.CreateDocumentUserShare,
			AppAction.CreateFolderLinkShare,
			AppAction.CreateFolderUserShare
		)
		.setPriority(MenuPriority.MEDIUMHIGH, 
			AppAction.Replace,	
			AppAction.CreateTask,
			AppAction.Download
		)
		.setPriority(MenuPriority.MEDIUM, 
			AppAction.Copy,
			AppAction.Rename,
			AppAction.Move
		)
		.setPriority(MenuPriority.MEDIUMLOW, 
			AppAction.Delete,
			AppAction.OpenInNew,
			AppAction.Favorite
		);
	
	Object.keys(contextMenuItems).forEach(k => copyToSelectionBar(k));
	
	// treat toolbar favorite items as having inverted labels/icons compared to context menu
	[
		sbaToolbarService.items[AppAction.Favorite],
		sbaSelectionDataStore.toolbar.items[AppAction.Favorite]
	]
	.forEach((i:DualMenuItem) => {
		let favoriteAction = sbActionRepository.getAction(AppAction.Favorite);
		let unfavoriteAction = sbActionRepository.getAction(AppAction.Unfavorite);
		
		let favoriteState = i.getState(AppAction.Favorite);
		favoriteState.icon = unfavoriteAction.icon;

		let unfavoriteState = i.getState(AppAction.Unfavorite);		
		unfavoriteState.icon = favoriteAction.icon;
	});

	/*----------- Feature-specific items ----------*/
	wopiToggleEnabled.then(enabled => {
		if (!enabled) {
			const items = [
				sbaSelectionDataStore.toolbar.getItem(AppAction.EditInShareBaseDesktop),
				sbaToolbarService.getItem(AppAction.EditInShareBaseDesktop),
				contextMenuItems[AppAction.EditInShareBaseDesktop]
			];
			items.forEach(i => {
				i.label = gettextCatalog.getString('Edit');
				i.icon = 'mode_edit';
			});
			return;
		}
		else {
			let editActions = [
				AppAction.EditInShareBaseDesktop,
				AppAction.EditInOfficeOnline
			];

			if (sbTrackingService.getExperimentVariant('edit-actions-order')) {
				editActions.reverse();
			}

			let editGroup = sbMenuItemFactory.createNested('editGroup', gettextCatalog.getString('Edit'), 'mode_edit');
			editGroup.priority = MenuPriority.HIGH;
			editActions.forEach(action => editGroup.menu.addItem(sbContextMenuService.getItem(action)));
	
			sbaToolbarService.removeItems(...editActions);
			sbaToolbarService.addItem(editGroup);
		}
	});

	/*---------- Menu item helpers ------*/
	function copyToToolbar(k:string, removeValidators?:boolean){
		sbaToolbarService.copyFromMenu(k, sbContextMenuService, removeValidators);
		return sbaToolbarService.getItem(k);
	}

	function copyToSelectionBar(k:string, removeValidators?:boolean){
		sbaSelectionDataStore.toolbar.copyFromMenu(k, sbContextMenuService, removeValidators);
		return sbaSelectionDataStore.toolbar.getItem(k);
	}
}

registerMenuItems.$inject = [
	'$mdMedia',
	'$state',
	'$stateParams',
	'$window',
	'pxAuthService',
	'pxEnvironmentService',
	'pxPhoenixToggleService',
	'sbDesktopService',
	'sbActionRepository',
	'sbContextMenuService',
	'sbToastService',
	'sbTrackingService',
	'sbMenuItemFactory',
	'SBA_GET_REQUEST_RIGHTS_ENABLED',
	'sbaToolbarService',
	'sbaSelectionDataStore',
	'staticToggleService',
	'gettextCatalog'
];


// WEBPACK FOOTER //
// ./src/sharebase-app/components/_app/helpers/registerMenuItems.ts